import {
  Component,
  effect,
  HostBinding,
  OnInit,
  DOCUMENT,
  inject,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CurrencyService } from '@services/currency.service';
import { CUSTOM_ICONS } from '@shared/constants/icons.const';
import { combineLatest, filter, take, map } from 'rxjs';

import { SessionService } from '@app/services/session.service';
import { Question, UserAction } from '@longnecktech/splash-commons-fe';
import { UserActionService } from '@services/user-action.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    '[class.new-styling]': 'newStylingEnabled()',
    '[class.scroll-enabled]': 'isScrollEnabled()',
  },
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly session = inject(SessionService);
  private readonly userActionService = inject(UserActionService);
  private document = inject<Document>(DOCUMENT);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private currencyService = inject(CurrencyService);

  theme$ = this.session.theme$;
  private readonly isMobile$ = toObservable(this.session.isMobile);
  newStylingEnabled = toSignal(
    this.theme$.pipe(map((theme) => !!theme?.newStylingEnabled)),
    { initialValue: false },
  );
  isScrollEnabled = toSignal(
    this.theme$.pipe(map((theme) => !!theme?.scrollEnabled)),
    { initialValue: false },
  );

  @HostBinding('class.flex') isFlex = false;

  constructor() {
    effect(() => {
      if (!this.session.user()) {
        return;
      }
      this.currencyService.setLanguage(this.session.user()!.language);
      console.log('user logged in', this.session.user()!.username);
    });

    /**
     * when we have actions for the quiz ready, we can use this to send the data to the backend
     */
    window.onbeforeunload = () => {
      if (!isEveryQuestionPicked(this.session.game()?.questions || [])) {
        const currentQuestion = this.session.currentQuestionId();
        const numberOfQuestions = this.session.game()?.questions.length || 0;
        this.userActionService
          .sendAction(
            UserAction.ABANDON_GAME,
            {
              progress: currentQuestion / numberOfQuestions,
            },
            this.session.game()!.uuid,
          )
          .pipe(take(1))
          .subscribe();
      }
    };

    function isEveryQuestionPicked(questions: Question[]): boolean {
      return questions.every((question) =>
        question.options.some((option) => option.isPicked),
      );
    }
  }

  ngOnInit(): void {
    this.createIcons();
    this.updateBodyClass();
    combineLatest([
      this.breakpointObserver.observe(['(max-width: 767.98px)']),
      this.route.queryParams,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([, queryParams]) => !!Object.keys(queryParams).length),
      )
      .subscribe(([result, queryParams]) => {
        if (result.matches) {
          this.session.isMobile.set(true);
          if (window.location.href.includes('/d')) {
            const currentPath = window.location.pathname;
            const locationMap: Record<string, string> = {
              '/d/play': '/m/game',
              '/d/play/summary': '/m/summary',
              '/d/play/deeplinking': '/m/deeplinking',
              '/d/results': '/m/results',
              '/d/ranking': '/m/ranking',
            };
            this.router.navigate([locationMap[currentPath] ?? '/m'], {
              queryParams,
            });
          }
        }
        if (!result.matches) {
          this.session.isMobile.set(false);
          if (
            window.location.href.includes('/m') ||
            !window.location.href.includes('/d')
          ) {
            const currentPath = window.location.pathname;
            const locationMap: Record<string, string> = {
              '/m/game': '/d/play',
              '/m/summary': '/d/play/summary',
              '/m/deeplinking': '/d/play/deeplinking',
              '/m/results': '/d/results',
              '/m/ranking': '/d/ranking',
            };
            this.router.navigate([locationMap[currentPath] ?? '/d'], {
              queryParams,
            });
          }
        }
      });
  }

  private updateBodyClass(): void {
    const bodyClass = 'mobile-version';
    this.isMobile$.pipe(untilDestroyed(this)).subscribe((isMobile) => {
      if (isMobile) {
        this.document.body.classList.add(bodyClass);
      } else {
        this.document.body.classList.remove(bodyClass);
      }
    });
    this.theme$
      .pipe(
        filter((theme) => !!theme),
        take(1),
      )
      .subscribe((theme) => {
        this.document.body.classList.toggle(
          'full-format',
          theme!.desktopFullResolutionFormat,
        );

        this.document.body.classList.toggle('light-mode', !theme?.darkMode);

        this.document.body.classList.toggle(
          'high-contrast',
          theme?.highContrastModeEnabled,
        );

        this.isFlex = !theme?.hideBackground;
      });
  }

  private createIcons(): void {
    CUSTOM_ICONS.forEach((icon) => {
      this.iconRegistry.addSvgIcon(
        icon.svg,
        this.sanitizer.bypassSecurityTrustResourceUrl(icon.path),
      );
    });
  }
}
